import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Generates page numbers with ellipsis for truncated ranges.
 * Pure function — unit test this directly.
 */
export function generatePageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 0) return [];
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, 4, '...', total];
  if (current >= total - 2) return [1, '...', total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
}

export interface PaginationControlsProps {
  /** Current active page (1-indexed) */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Total item count */
  totalItems: number;
  /** Number of items currently displayed on this page */
  currentPageItemCount: number;
  /** Items per page */
  pageSize: number;
  /** Available page size options */
  pageSizeOptions?: number[];
  /** Called when user changes page */
  onPageChange: (page: number) => void;
  /** Called when user changes page size */
  onPageSizeChange?: (size: number) => void;
  /** Show the rows-per-page selector (default: true) */
  showPageSizeSelector?: boolean;
  /** Additional className for the container */
  className?: string;
}

export function PaginationControls({
  page,
  totalPages,
  totalItems,
  currentPageItemCount,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  className,
}: PaginationControlsProps) {
  const offset = (page - 1) * pageSize;

  return (
    <div className={cn(
      "flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-6 border-t border-border",
      className
    )}>
      {/* Left: info + rows-per-page */}
      <div className="flex items-center gap-3">
        <p className="text-xs sm:text-sm text-muted-foreground" data-testid="text-pagination-info">
          Showing {Math.min(offset + 1, totalItems)}-{Math.min(offset + currentPageItemCount, totalItems)} of {totalItems}
        </p>
        {showPageSizeSelector && onPageSizeChange && (
          <>
            <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
              <SelectTrigger className="w-[70px] h-8" data-testid="select-rows-per-page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((opt) => (
                  <SelectItem key={opt} value={String(opt)}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground hidden sm:inline">per page</span>
          </>
        )}
      </div>

      {/* Right: page controls */}
      <div className="flex items-center space-x-1">
        <Button variant="outline" size="sm" disabled={page === 1}
          onClick={() => onPageChange(1)} data-testid="button-first-page" className="h-8"
          aria-label="First page">
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" disabled={page === 1}
          onClick={() => onPageChange(page - 1)} data-testid="button-previous-page" className="h-8"
          aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {generatePageNumbers(page, totalPages).map((item, i) =>
          item === '...' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-muted-foreground select-none">...</span>
          ) : (
            <Button key={item} variant={page === item ? "default" : "outline"} size="sm"
              onClick={() => onPageChange(item as number)}
              aria-label={`Page ${item}`}
              aria-current={page === item ? "page" : undefined}
              data-testid={`button-page-${item}`} className="h-8 min-w-[32px]">
              {item}
            </Button>
          )
        )}

        <Button variant="outline" size="sm" disabled={page === totalPages || totalPages === 0}
          onClick={() => onPageChange(page + 1)} data-testid="button-next-page" className="h-8"
          aria-label="Next page">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" disabled={page === totalPages || totalPages === 0}
          onClick={() => onPageChange(totalPages)} data-testid="button-last-page" className="h-8"
          aria-label="Last page">
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
